import type { Point } from "../../shared/contracts.js";
import type { CanvasBox, LayoutOptions } from "./canvasGeometry.js";

type Graph = Map<string, Set<string>>;
const ordered = (values: Iterable<string>) => [...values].sort((a, b) => a.localeCompare(b));

/** Layout one connected component in local coordinates. All traversal and iteration
 * limits depend on graph size, never on wall-clock time, so previews are repeatable. */
export function componentLayout(
  boxes: CanvasBox[], adjacency: Graph, children: Graph, options: LayoutOptions,
): { positions: Record<string, Point>; warnings: string[] } {
  const horizontal = options.direction === "left" || options.direction === "right";
  const reverse = options.direction === "left" || options.direction === "up";
  const gap = Math.max(40, Number.isFinite(options.gap) ? options.gap : 80);
  const byId = new Map(boxes.map((box) => [box.id, box]));
  const crossSize = (id: string) => horizontal ? byId.get(id)!.height : byId.get(id)!.width;
  const axisSize = (id: string) => horizontal ? byId.get(id)!.width : byId.get(id)!.height;
  const incoming = new Map(boxes.map((box) => [box.id, 0]));
  boxes.forEach((box) => children.get(box.id)?.forEach((id) => {
    if (byId.has(id)) incoming.set(id, incoming.get(id)! + 1);
  }));
  const requested = boxes.find((box) => box.id === options.rootId || box.objectId === options.rootId);
  const roots = requested ? [requested.id] : boxes.filter((box) => !incoming.get(box.id)).map((box) => box.id);
  if (!roots.length && boxes.length) roots.push([...boxes].sort((a, b) =>
    (children.get(b.id)?.size ?? 0) - (children.get(a.id)?.size ?? 0) || a.id.localeCompare(b.id))[0]!.id);
  const forest = new Map(boxes.map((box) => [box.id, [] as string[]]));
  const levels = new Map<string, number>();
  const traversal: string[] = [];
  const seeds = [...roots, ...boxes.map((box) => box.id)];
  for (const seed of seeds) {
    if (levels.has(seed)) continue;
    levels.set(seed, 0);
    const queue = [seed];
    for (let i = 0; i < queue.length; i++) {
      const id = queue[i]!;
      traversal.push(id);
      const directed = ordered(children.get(id) ?? []).filter((next) => byId.has(next));
      // A selected root is an undirected tree root: retain every node even when
      // relationships point toward it. Other modes preserve semantic direction.
      const neighbors = options.mode === "tree" || requested
        ? [...directed, ...ordered(adjacency.get(id) ?? []).filter((next) => !directed.includes(next))]
        : directed;
      for (const next of neighbors) {
        if (!byId.has(next) || levels.has(next)) continue;
        levels.set(next, levels.get(id)! + 1);
        forest.get(id)!.push(next);
        queue.push(next);
      }
    }
  }
  // Kahn's count detects feedback even when a cycle is reachable from a root.
  const pending = new Map(incoming), ready = ordered([...pending].filter(([, degree]) => !degree).map(([id]) => id));
  let acyclicCount = 0;
  for (let i = 0; i < ready.length; i++) {
    acyclicCount++;
    for (const next of ordered(children.get(ready[i]!) ?? [])) {
      if (!pending.has(next)) continue;
      pending.set(next, pending.get(next)! - 1);
      if (!pending.get(next)) ready.push(next);
    }
  }
  const warnings: string[] = acyclicCount < boxes.length
    ? ["A cycle is preserved. Feedback relationships may run against the dominant direction."] : [];
  const rows = new Map<number, string[]>();
  traversal.forEach((id) => { const level = levels.get(id)!; rows.set(level, [...(rows.get(level) ?? []), id]); });
  const rowAxis = new Map<number, number>();
  let axis = 0;
  for (const [rank, row] of [...rows].sort((a, b) => a[0] - b[0])) {
    rowAxis.set(rank, axis);
    axis += Math.max(...row.map(axisSize)) + gap + (options.mode === "orthogonal" ? 100 : 48);
  }
  const raw: Record<string, Point> = {};
  const isForest = acyclicCount === boxes.length && [...incoming.values()].every((degree) => degree <= 1);
  if (options.mode === "tree" || isForest) {
    const spans = new Map<string, number>();
    for (const id of [...traversal].reverse()) {
      const childIds = forest.get(id)!;
      spans.set(id, Math.max(crossSize(id), childIds.reduce((sum, next) => sum + spans.get(next)!, 0) + Math.max(0, childIds.length - 1) * gap));
    }
    const left = new Map<string, number>();
    let rootLeft = 0;
    for (const id of traversal) {
      if (!left.has(id)) { left.set(id, rootLeft); rootLeft += spans.get(id)! + gap; }
      const span = spans.get(id)!;
      raw[id] = { x: left.get(id)! + (span - crossSize(id)) / 2, y: rowAxis.get(levels.get(id)!)! };
      const childIds = forest.get(id)!;
      const total = childIds.reduce((sum, next) => sum + spans.get(next)!, 0) + Math.max(0, childIds.length - 1) * gap;
      let childLeft = left.get(id)! + (span - total) / 2;
      for (const next of childIds) { left.set(next, childLeft); childLeft += spans.get(next)! + gap; }
    }
  } else {
    // Alternating sweeps consider both parents and children; stable ties preserve
    // reproducibility. Normalized positions account for unequal rank lengths.
    const ranks = [...rows.keys()].sort((a, b) => a - b);
    for (let pass = 0; pass < 8; pass++) {
      const down = pass % 2 === 0;
      for (const rank of down ? ranks : [...ranks].reverse()) {
        const row = rows.get(rank)!;
        const scores = new Map(row.map((id, index) => {
          const peers = ordered(adjacency.get(id) ?? []).filter((peer) => byId.has(peer) && (down ? levels.get(peer)! < rank : levels.get(peer)! > rank));
          const score = peers.length ? peers.reduce((sum, peer) => {
            const peerRow = rows.get(levels.get(peer)!)!;
            return sum + (peerRow.indexOf(peer) + 0.5) / peerRow.length;
          }, 0) / peers.length : (index + 0.5) / row.length;
          return [id, score] as const;
        }));
        row.sort((a, b) => scores.get(a)! - scores.get(b)! || a.localeCompare(b));
      }
    }
    const rowWidth = (row: string[]) => row.reduce((sum, id) => sum + crossSize(id), 0) + Math.max(0, row.length - 1) * gap;
    const widest = Math.max(0, ...[...rows.values()].map(rowWidth));
    for (const [rank, row] of rows) {
      let cross = (widest - rowWidth(row)) / 2;
      for (const id of row) { raw[id] = { x: cross, y: rowAxis.get(rank)! }; cross += crossSize(id) + gap; }
    }
  }

  if (options.mode === "circular" || options.mode === "clustered" || (acyclicCount < boxes.length && boxes.length <= 3)) {
    // DFS keeps each subtree contiguous around the circle, reducing interleaved
    // chords compared with arbitrary object-ID order.
    const seen = new Set<string>(), order: string[] = [];
    for (const seed of [...roots, ...boxes.map((box) => box.id)]) {
      const stack = [seed];
      while (stack.length) {
        const id = stack.pop()!;
        if (seen.has(id)) continue;
        seen.add(id); order.push(id);
        stack.push(...ordered(adjacency.get(id) ?? []).filter((next) => byId.has(next) && !seen.has(next)).reverse());
      }
    }
    if (options.mode === "clustered") order.sort((a, b) => a.localeCompare(b));
    const maxDiameter = Math.max(0, ...boxes.map((box) => Math.hypot(box.width, box.height)));
    const radius = boxes.length < 2 ? 0 : (maxDiameter + gap + 32) / (2 * Math.sin(Math.PI / boxes.length));
    order.forEach((id, i) => {
      const angle = -Math.PI / 2 + i * 2 * Math.PI / order.length;
      raw[id] = { x: Math.cos(angle) * radius - crossSize(id) / 2, y: Math.sin(angle) * radius - axisSize(id) / 2 };
    });
  }
  if (options.mode === "symmetric" && boxes.length <= 200) {
    // Deterministic spring relaxation from a layered seed. Finite iterations and
    // a displacement cap keep dense components responsive and prevent divergence.
    const centers = boxes.map((box) => ({ x: raw[box.id]!.x + crossSize(box.id) / 2, y: raw[box.id]!.y + axisSize(box.id) / 2 }));
    const indexes = new Map(boxes.map((box, index) => [box.id, index]));
    const links = boxes.flatMap((box, i) => ordered(adjacency.get(box.id) ?? []).filter((id) => indexes.has(id) && indexes.get(id)! > i).map((id) => [i, indexes.get(id)!] as const));
    for (let pass = 0; pass < 96; pass++) {
      const delta = centers.map(() => ({ x: 0, y: 0 }));
      for (let i = 0; i < centers.length; i++) for (let j = i + 1; j < centers.length; j++) {
        let dx = centers[j]!.x - centers[i]!.x, dy = centers[j]!.y - centers[i]!.y;
        if (Math.abs(dx) + Math.abs(dy) < 0.01) { dx = 1; dy = (i + j) % 2 ? 1 : -1; }
        const length = Math.hypot(dx, dy);
        const spacing = (Math.hypot(boxes[i]!.width, boxes[i]!.height) + Math.hypot(boxes[j]!.width, boxes[j]!.height)) / 2 + gap;
        const force = Math.min(100, spacing * spacing / (length * length) * 14);
        delta[i]!.x -= dx / length * force; delta[i]!.y -= dy / length * force;
        delta[j]!.x += dx / length * force; delta[j]!.y += dy / length * force;
      }
      for (const [i, j] of links) {
        const dx = centers[j]!.x - centers[i]!.x, dy = centers[j]!.y - centers[i]!.y, length = Math.hypot(dx, dy) || 1;
        const ideal = (Math.hypot(boxes[i]!.width, boxes[i]!.height) + Math.hypot(boxes[j]!.width, boxes[j]!.height)) / 2 + gap * 1.5;
        const force = (length - ideal) * 0.045;
        delta[i]!.x += dx / length * force; delta[i]!.y += dy / length * force;
        delta[j]!.x -= dx / length * force; delta[j]!.y -= dy / length * force;
      }
      const limit = 32 * (1 - pass / 110);
      centers.forEach((point, i) => {
        const length = Math.hypot(delta[i]!.x, delta[i]!.y) || 1, scale = Math.min(1, limit / length);
        point.x += delta[i]!.x * scale; point.y += delta[i]!.y * scale;
      });
    }
    boxes.forEach((box, i) => { raw[box.id] = { x: centers[i]!.x - crossSize(box.id) / 2, y: centers[i]!.y - axisSize(box.id) / 2 }; });
  } else if (options.mode === "symmetric" && boxes.length > 200) {
    warnings.push("This component exceeds 200 entities. A bounded hierarchical arrangement was used for the symmetric preview.");
  }
  const positions: Record<string, Point> = {};
  boxes.forEach((box) => {
    const point = raw[box.id]!;
    const main = reverse ? -point.y - axisSize(box.id) : point.y;
    positions[box.id] = horizontal ? { x: main, y: point.x } : { x: point.x, y: main };
  });
  return { positions, warnings };
}
